/**
 * The reusable newsletter-workspace PANELS plus their pure helpers. These are the
 * same content-design and operations panels the single newsletter page used to
 * host inline; the Phase 0 route split moved them here unchanged so the Design
 * studio and the Operate surface can each compose the subset they own. Nothing in
 * this module loads the newsletter or knows about routing: it takes an `ApiDeps`
 * and the already-loaded data as props and hands edits back up through callbacks.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { Link } from 'react-router-dom';
import {
  AUDIENCES,
  CADENCES,
  NEWSLETTER_PURPOSE_KINDS,
  SOCIAL_PLATFORMS,
  type Cadence,
  type LikedNewsletter,
  type Newsletter,
  type NewsletterConfig,
  type NewsletterPurposeKind,
  type Social,
  type SocialPlatform,
} from '@nuff/types';
import { Badge, Button, Field, Notification } from '@nuff/ui/react';

import {
  addSubscriber,
  importSubscribers,
  importSubscribersFromFile,
  listSentIssues,
  listSubscribers,
  proposeStructure,
  refineNewsletter,
  removeSubscriber,
  scheduleNewsletter,
  sendIssue,
  sendIssueTest,
  updateNewsletterConfig,
  updateSections,
  type ApiDeps,
  type Issue,
  type SectionSelection,
  type SentIssue,
  type Subscriber,
  type SubscriberImportResult,
  type SuggestedSection,
} from '../../api';
import { TOPIC_PRESETS } from '../../wizard/presets';

/** Discovery state for the newsletter's suggested sections. */
export type DiscoverState = 'idle' | 'loading' | 'discovering' | 'ready' | 'error';

/** Save state for the config panel. */
type SaveState = 'idle' | 'saving' | 'saved' | 'error';

// Topics that read as local get a location nudge. We match a few plain keywords
// plus the wizard's own "Local and community" preset labels, so a tap-selected
// local topic is recognised even without the word "local" in it.
const LOCAL_KEYWORDS = ['local', 'city', 'near me', 'neighborhood', 'neighbourhood'];
const LOCAL_PRESET_LABELS = new Set(
  TOPIC_PRESETS.filter((topic) => topic.vertical === 'Local and community').map((topic) =>
    topic.label.toLowerCase(),
  ),
);

/** True when a single topic reads as place-specific (so discovery wants a location). */
export function topicLooksLocal(topic: string): boolean {
  const lower = topic.trim().toLowerCase();
  if (lower.length === 0) return false;
  if (LOCAL_PRESET_LABELS.has(lower)) return true;
  return LOCAL_KEYWORDS.some((keyword) => lower.includes(keyword));
}

/** True when any of the topics reads as local. */
function anyTopicLooksLocal(topics: readonly string[]): boolean {
  return topics.some((topic) => topicLooksLocal(topic));
}

/** A human, jargon-free description of the chosen writing voice. */
function voiceSummary(newsletter: Newsletter): string {
  const voice = newsletter.voice;
  if (voice === undefined) return 'No voice chosen yet.';
  // A pasted writing SAMPLE, distinct from a described tone (see the Tone line below).
  if (voice.kind === 'custom') return 'A writing sample you pasted for our AI to match.';
  // Preset ids are short slugs ("warm", "sharp"); title-case for display.
  const name = voice.id.charAt(0).toUpperCase() + voice.id.slice(1);
  return `The "${name}" preset voice.`;
}

function freshnessLabel(freshnessHours: number | null): string | null {
  if (freshnessHours === null) return null;
  if (freshnessHours < 1) return 'Updated within the hour';
  if (freshnessHours < 24) return `Freshest ~${String(Math.round(freshnessHours))}h ago`;
  return `Freshest ~${String(Math.round(freshnessHours / 24))}d ago`;
}

/** Friendly, jargon-free cadence labels for the picker. */
const CADENCE_LABELS: Record<Cadence, string> = {
  weekly: 'Weekly',
  biweekly: 'Every two weeks',
  monthly: 'Monthly',
};

/** Format an ISO date as a friendly long date, e.g. "Tuesday, August 11, 2026". */
export function formatIssueDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** Split a comma-separated topics field into clean topic strings. */
function parseTopicsText(text: string): string[] {
  return text
    .split(',')
    .map((topic) => topic.trim())
    .filter((topic) => topic.length > 0);
}

/** Friendly, jargon-free labels for each social platform. */
const SOCIAL_LABELS: Record<SocialPlatform, string> = {
  x: 'X',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  website: 'Website',
  other: 'Other',
};

/** Best-effort friendly label for a URL: its host without a leading www. */
function labelFromUrl(url: string): string {
  try {
    const host = new URL(url.includes('://') ? url : `https://${url}`).hostname;
    return host.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * The real publication names bound to a section: the names of its SITE-FEED
 * sources (a site is represented as itself). Used so a section surfaces "ESPN,
 * The Athletic" rather than a raw search query where possible.
 */
function sectionPublications(section: SuggestedSection): string[] {
  const names: string[] = [];
  for (const source of section.sources) {
    if (source.sourceType === 'site-feed') {
      names.push(source.siteName);
    }
  }
  return names;
}

/** A short "From ESPN, The Athletic" line, or a plain source count fallback. */
function sourcesLine(section: SuggestedSection): string {
  const pubs = sectionPublications(section);
  if (pubs.length > 0) {
    const shown = pubs.slice(0, 3).join(', ');
    return pubs.length > 3 ? `From ${shown} and more` : `From ${shown}`;
  }
  const count = section.sources.length;
  return `${String(count)} ${count === 1 ? 'source' : 'sources'}`;
}

/** The selected sections in their curated running order (unset order last). */
export function selectedInOrder(sections: readonly SuggestedSection[]): SuggestedSection[] {
  return sections
    .filter((section) => section.status === 'selected')
    .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER));
}

/** The non-dismissed sections in reading order: selected first (by order), then the rest. */
export function orderedSections(sections: readonly SuggestedSection[]): SuggestedSection[] {
  const visible = sections.filter((section) => section.status !== 'dismissed');
  const selected = selectedInOrder(visible);
  // Non-selected sections are sorted STABLY (by any order, then title) so an
  // unrelated refine turn that returns the same set in a different array order
  // cannot reshuffle the list under the owner.
  const rest = visible
    .filter((section) => section.status !== 'selected')
    .sort(
      (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER) ||
        a.title.localeCompare(b.title),
    );
  return [...selected, ...rest];
}

/**
 * A stable signature of the SELECTED sections in their curated order, used to
 * trigger an auto-rebuild of the preview whenever the curation changes. A space
 * delimiter is safe because section ids never contain spaces.
 */
export function selectionKeyOf(sections: readonly SuggestedSection[]): string {
  return selectedInOrder(sections)
    .map((section) => section.sectionId)
    .join(' ');
}

/**
 * Apply a batch of selection updates to the sections in memory, mirroring what the
 * server does, so the UI can update optimistically before the round-trip lands.
 * Selected sections are renumbered to a dense 0-based order, exactly as the server
 * renumbers them, so the optimistic view matches the persisted one.
 */
export function applyUpdatesLocally(
  sections: readonly SuggestedSection[],
  updates: readonly SectionSelection[],
): SuggestedSection[] {
  const byId = new Map(updates.map((update) => [update.sectionId, update]));
  const next = sections.map((section) => {
    const update = byId.get(section.sectionId);
    if (update === undefined) return section;
    const applied: SuggestedSection = { ...section, status: update.status };
    if (update.order !== undefined) {
      return { ...applied, order: update.order };
    }
    // A move to non-selected drops any stale running-order position.
    if (update.status !== 'selected') {
      const { order: _dropped, ...rest } = applied;
      return rest;
    }
    return applied;
  });
  // Renumber the selected sections densely, matching the server's renumber pass.
  const orderMap = new Map(
    selectedInOrder(next).map((section, index) => [section.sectionId, index]),
  );
  return next.map((section) =>
    section.status === 'selected'
      ? { ...section, order: orderMap.get(section.sectionId) ?? 0 }
      : section,
  );
}

/**
 * One selectable section row: a keep/leave toggle, a dismiss control, the real
 * publication names, and the plain summary. Selected sections also get up/down
 * reorder controls so the owner can arrange the running order by hand.
 */
function SectionRow({
  section,
  selectedIndex,
  selectedCount,
  busy,
  onToggle,
  onDismiss,
  onMove,
}: {
  readonly section: SuggestedSection;
  readonly selectedIndex: number;
  readonly selectedCount: number;
  readonly busy: boolean;
  readonly onToggle: (section: SuggestedSection) => void;
  readonly onDismiss: (section: SuggestedSection) => void;
  readonly onMove: (section: SuggestedSection, direction: -1 | 1) => void;
}): React.JSX.Element {
  const isSelected = section.status === 'selected';
  const freshness = freshnessLabel(section.metrics.freshnessHours);
  return (
    <li className={`nlv__section-item${isSelected ? ' nlv__section-item--selected' : ''}`}>
      <div className="nlv__section-main">
        <label className="nlv__section-check">
          <input
            type="checkbox"
            checked={isSelected}
            disabled={busy}
            aria-label={`Include ${section.title}`}
            onChange={() => onToggle(section)}
          />
          <span className="dash__section-title">{section.title}</span>
        </label>
        <p className="dash__section-summary">{section.summary}</p>
        <p className="nlv__section-meta">
          <span className="nlv__section-count">{sourcesLine(section)}</span>
          {freshness !== null && <span className="nlv__section-count">{freshness}</span>}
        </p>
      </div>
      <div className="nlv__section-controls">
        {isSelected && (
          <div className="nlv__section-order" role="group" aria-label="Reorder section">
            <button
              type="button"
              className="nlv__order-btn"
              aria-label={`Move ${section.title} up`}
              disabled={busy || selectedIndex === 0}
              onClick={() => onMove(section, -1)}
            >
              ↑
            </button>
            <button
              type="button"
              className="nlv__order-btn"
              aria-label={`Move ${section.title} down`}
              disabled={busy || selectedIndex === selectedCount - 1}
              onClick={() => onMove(section, 1)}
            >
              ↓
            </button>
          </div>
        )}
        <button
          type="button"
          className="nlv__section-dismiss"
          aria-label={`Hide ${section.title}`}
          disabled={busy}
          onClick={() => onDismiss(section)}
        >
          Hide
        </button>
      </div>
    </li>
  );
}

/**
 * The curated, selectable sources/structure list: the primary content of the
 * left column. Renders every non-dismissed section as a keep/leave row, offers a
 * "Propose structure" primary action that asks our AI to arrange them into an
 * ordered running order, and a ghost rediscover. Selection, dismissal, reorder,
 * and propose all persist through the API and hand the fresh list back up.
 */
export function SectionsPanel({
  deps,
  newsletterId,
  hasTopics,
  sections,
  state,
  error,
  busy,
  onRediscover,
  onSectionsChange,
  setupTo,
}: {
  readonly deps: ApiDeps;
  readonly newsletterId: string;
  readonly hasTopics: boolean;
  readonly sections: readonly SuggestedSection[];
  readonly state: DiscoverState;
  readonly error: string | null;
  readonly busy: boolean;
  readonly onRediscover: () => void;
  readonly onSectionsChange: (sections: readonly SuggestedSection[]) => void;
  /** Router path to the Basics setup editor, where topics are actually edited. */
  readonly setupTo: string;
}): React.JSX.Element {
  const [saveError, setSaveError] = useState<string | null>(null);
  const [proposing, setProposing] = useState(false);
  const [proposeNote, setProposeNote] = useState<string | null>(null);

  const visible = useMemo(() => orderedSections(sections), [sections]);
  const selected = useMemo(() => selectedInOrder(sections), [sections]);

  // The latest sections, held in a ref so an optimistic persist can roll back to
  // exactly what was on screen when it started, even after quick successive edits.
  const sectionsRef = useRef(sections);
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  /**
   * Persist a batch of selection updates OPTIMISTICALLY: apply them to the list
   * immediately (so toggling/reordering/hiding feels instant and never disables
   * the whole list), then reconcile with the server's fresh list on success, or
   * roll back to the pre-edit list and surface an error on failure.
   */
  const persist = useCallback(
    (updates: SectionSelection[]): void => {
      const before = sectionsRef.current;
      const optimistic = applyUpdatesLocally(before, updates);
      onSectionsChange(optimistic);
      setSaveError(null);
      setProposeNote(null);
      updateSections(deps, newsletterId, updates)
        .then((result) => {
          onSectionsChange(result.sections);
        })
        .catch((caught: unknown) => {
          // Roll back to exactly what was shown before this edit.
          onSectionsChange(before);
          setSaveError(caught instanceof Error ? caught.message : 'Could not save your choices.');
        });
    },
    [deps, newsletterId, onSectionsChange],
  );

  const toggle = useCallback(
    (section: SuggestedSection): void => {
      if (section.status === 'selected') {
        persist([{ sectionId: section.sectionId, status: 'suggested' }]);
      } else {
        // Selecting appends the section to the end of the running order.
        persist([{ sectionId: section.sectionId, status: 'selected', order: selected.length }]);
      }
    },
    [persist, selected.length],
  );

  const dismiss = useCallback(
    (section: SuggestedSection): void => {
      persist([{ sectionId: section.sectionId, status: 'dismissed' }]);
    },
    [persist],
  );

  const move = useCallback(
    (section: SuggestedSection, direction: -1 | 1): void => {
      const index = selected.findIndex((item) => item.sectionId === section.sectionId);
      const swapWith = index + direction;
      if (index === -1 || swapWith < 0 || swapWith >= selected.length) {
        return;
      }
      const other = selected[swapWith];
      if (other === undefined) return;
      // Swap the two sections' order positions; everything else stays put.
      persist([
        { sectionId: section.sectionId, status: 'selected', order: swapWith },
        { sectionId: other.sectionId, status: 'selected', order: index },
      ]);
    },
    [persist, selected],
  );

  const propose = useCallback(async (): Promise<void> => {
    setProposing(true);
    setSaveError(null);
    setProposeNote(null);
    try {
      const result = await proposeStructure(deps, newsletterId);
      onSectionsChange(result.sections);
      if (result.kept !== undefined && result.setAside !== undefined) {
        setProposeNote(`Kept ${String(result.kept)}, set aside ${String(result.setAside)}.`);
      }
    } catch (caught) {
      setSaveError(caught instanceof Error ? caught.message : 'Could not propose a structure.');
    } finally {
      setProposing(false);
    }
  }, [deps, newsletterId, onSectionsChange]);

  // Row controls stay live during a background save (optimistic UI); they are only
  // disabled while discovery/propose is actively replacing the whole list.
  const rowBusy = proposing || busy;

  return (
    <section className="nlv__panel nlv__structure" aria-labelledby="nlv-structure">
      <div className="nlv__structure-head">
        <h2 id="nlv-structure" className="nlv__panel-title">
          Your sources and structure
        </h2>
        <p className="nlv__panel-lead">
          Keep the sections you want, hide the rest, and put them in the order readers should meet
          them. Our AI can propose a running order for you.
        </p>
      </div>

      <div className="nlv__structure-actions">
        <Button
          loading={proposing}
          onClick={() => void propose()}
          disabled={rowBusy || sections.length === 0}
        >
          {proposing ? 'Proposing…' : 'Propose structure'}
        </Button>
        <Button variant="secondary" onClick={onRediscover} disabled={rowBusy || !hasTopics}>
          {state === 'discovering' ? 'Finding sources…' : 'Find more sources'}
        </Button>
      </div>

      {state === 'discovering' && (
        <p className="dash__status" role="status">
          Finding sources our AI can build sections from…
        </p>
      )}

      {state === 'error' && error !== null && <Notification variant="error">{error}</Notification>}

      {saveError !== null && <Notification variant="error">{saveError}</Notification>}

      {proposeNote !== null && (
        <p className="nlv__propose-note" role="status">
          {proposeNote}
        </p>
      )}

      {!hasTopics && (
        <p className="dash__hint">
          Add at least one topic so our AI can find sources.{' '}
          <Link className="nlv__inline-link" to={setupTo}>
            Edit your newsletter&rsquo;s basics
          </Link>
        </p>
      )}

      {state === 'ready' && hasTopics && sections.length === 0 && (
        <p className="dash__empty">No sources found yet. Try Find more sources.</p>
      )}

      {visible.length > 0 && (
        <ul className="nlv__section-list">
          {visible.map((section) => {
            const selectedIndex =
              section.status === 'selected'
                ? selected.findIndex((item) => item.sectionId === section.sectionId)
                : -1;
            return (
              <SectionRow
                key={section.sectionId}
                section={section}
                selectedIndex={selectedIndex}
                selectedCount={selected.length}
                busy={rowBusy}
                onToggle={toggle}
                onDismiss={dismiss}
                onMove={move}
              />
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * The full "About this newsletter" config editor: every field the wizard
 * captured, saved through `updateNewsletterConfig`. In v2 it lives inside the
 * collapsed "Edit setup" disclosure so it no longer dominates the page.
 */
function ConfigEditor({
  deps,
  newsletter,
  onUpdated,
}: {
  readonly deps: ApiDeps;
  readonly newsletter: Newsletter;
  readonly onUpdated: (updated: Newsletter) => void;
}): React.JSX.Element {
  const [topicsText, setTopicsText] = useState(newsletter.topics.join(', '));
  const [tagline, setTagline] = useState(newsletter.tagline ?? '');
  const [location, setLocation] = useState(newsletter.location ?? '');
  const [timezone, setTimezone] = useState(newsletter.timezone ?? '');
  const [authorName, setAuthorName] = useState(newsletter.authorName ?? '');
  const [authorBio, setAuthorBio] = useState(newsletter.authorBio ?? '');
  const [fromName, setFromName] = useState(newsletter.fromName ?? '');
  const [replyEmail, setReplyEmail] = useState(newsletter.replyEmail ?? '');
  const [socials, setSocials] = useState<readonly Social[]>(newsletter.socials ?? []);
  const [purposeKind, setPurposeKind] = useState<NewsletterPurposeKind | ''>(
    newsletter.purpose?.kind ?? '',
  );
  const [purposeText, setPurposeText] = useState(newsletter.purpose?.text ?? '');
  const [voiceText, setVoiceText] = useState(
    newsletter.voice?.kind === 'custom' ? newsletter.voice.text : '',
  );
  const [liked, setLiked] = useState<readonly LikedNewsletter[]>(newsletter.likedNewsletters ?? []);
  const [linkLabel, setLinkLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reseed the form whenever the newsletter prop changes (e.g. a refine-chat edit
  // added a topic or set a tone), so the fields and the live newsletter stay
  // coherent and a subsequent Save sends the CURRENT values, not the stale ones the
  // form was first seeded with. Only fires on an actual prop change (the parent
  // keeps the same object reference across unrelated re-renders), so it never
  // clobbers what the owner is typing. saveState/saveError are deliberately left
  // alone so a just-shown "Saved." confirmation survives the post-save reseed.
  useEffect(() => {
    setTopicsText(newsletter.topics.join(', '));
    setTagline(newsletter.tagline ?? '');
    setLocation(newsletter.location ?? '');
    setTimezone(newsletter.timezone ?? '');
    setAuthorName(newsletter.authorName ?? '');
    setAuthorBio(newsletter.authorBio ?? '');
    setFromName(newsletter.fromName ?? '');
    setReplyEmail(newsletter.replyEmail ?? '');
    setSocials(newsletter.socials ?? []);
    setPurposeKind(newsletter.purpose?.kind ?? '');
    setPurposeText(newsletter.purpose?.text ?? '');
    setVoiceText(newsletter.voice?.kind === 'custom' ? newsletter.voice.text : '');
    setLiked(newsletter.likedNewsletters ?? []);
  }, [newsletter]);

  const parsedTopics = parseTopicsText(topicsText);
  const showLocationPrompt = anyTopicLooksLocal(parsedTopics) && location.trim().length === 0;

  const addLink = useCallback((): void => {
    const url = linkUrl.trim();
    if (url.length === 0) return;
    const label = linkLabel.trim().length > 0 ? linkLabel.trim() : labelFromUrl(url);
    setLiked((prev) => [...prev, { label, url }]);
    setLinkLabel('');
    setLinkUrl('');
  }, [linkLabel, linkUrl]);

  const removeLink = useCallback((index: number): void => {
    setLiked((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const addSocial = useCallback((): void => {
    setSocials((prev) => [...prev, { platform: SOCIAL_PLATFORMS[0], handle: '' }]);
  }, []);

  const updateSocial = useCallback((index: number, patch: Partial<Social>): void => {
    setSocials((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }, []);

  const removeSocial = useCallback((index: number): void => {
    setSocials((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const save = useCallback(async (): Promise<void> => {
    setSaveState('saving');
    setSaveError(null);
    // Build the config from the current form. Lists (topics, liked, socials) are
    // always sent so removals persist; single text fields are only sent when
    // non-empty, since the backend sets exactly the keys present and we never
    // want to clobber a field we are not editing (e.g. a preset voice).
    const cleanedSocials: Social[] = socials
      .map((item) => ({ platform: item.platform, handle: item.handle.trim() }))
      .filter((item) => item.handle.length > 0);
    const config: NewsletterConfig = {
      topics: parsedTopics,
      likedNewsletters: [...liked],
      socials: cleanedSocials,
    };
    if (tagline.trim().length > 0) {
      config.tagline = tagline.trim();
    }
    if (location.trim().length > 0) {
      config.location = location.trim();
    }
    if (timezone.trim().length > 0) {
      config.timezone = timezone.trim();
    }
    if (authorName.trim().length > 0) {
      config.authorName = authorName.trim();
    }
    if (authorBio.trim().length > 0) {
      config.authorBio = authorBio.trim();
    }
    if (fromName.trim().length > 0) {
      config.fromName = fromName.trim();
    }
    if (replyEmail.trim().length > 0) {
      config.replyEmail = replyEmail.trim();
    }
    if (purposeKind !== '') {
      config.purpose = {
        kind: purposeKind,
        ...(purposeText.trim().length > 0 ? { text: purposeText.trim() } : {}),
      };
      // Keep audience aligned with purpose so discovery stays coherent: a company
      // purpose implies a company audience, everything else a community one.
      config.audience = purposeKind === 'company' ? AUDIENCES[0] : AUDIENCES[1];
    }
    if (voiceText.trim().length > 0) {
      config.voice = { kind: 'custom', text: voiceText.trim() };
    }
    try {
      const updated = await updateNewsletterConfig(deps, newsletter.newsletterId, config);
      onUpdated(updated);
      setSaveState('saved');
    } catch (caught) {
      setSaveError(caught instanceof Error ? caught.message : 'Could not save your changes.');
      setSaveState('error');
    }
  }, [
    authorBio,
    authorName,
    deps,
    fromName,
    liked,
    location,
    newsletter.newsletterId,
    onUpdated,
    parsedTopics,
    purposeKind,
    purposeText,
    replyEmail,
    socials,
    tagline,
    timezone,
    voiceText,
  ]);

  return (
    <div className="nlv__config" aria-labelledby="nlv-about">
      {/* h2 (not h3) so the Operate heading order is h1 -> h2 (setup) -> h2
          (schedule/subscribers): the setup panel leads the screen, so its top
          heading must sit at the same level as the sibling panel headings, with
          the field subgroups (Identity/Focus/...) as its h3 children. */}
      <h2 id="nlv-about" className="nlv__group-title nlv__config-title">
        About this newsletter
      </h2>

      <div className="nlv__group">
        <h3 className="nlv__group-title">Identity</h3>

        <Field
          label="Tagline"
          name="tagline"
          value={tagline}
          placeholder="One line on what this newsletter is"
          onChange={setTagline}
        />

        <Field
          label="Why it exists"
          name="purpose-kind"
          value={purposeKind}
          options={[
            { value: '', label: 'Not set' },
            ...NEWSLETTER_PURPOSE_KINDS.map((kind) => ({
              value: kind,
              label:
                kind === 'company'
                  ? 'For a company or team'
                  : kind === 'community'
                    ? 'For a community'
                    : 'Something else',
            })),
          ]}
          onChange={(value) => setPurposeKind(value as NewsletterPurposeKind | '')}
        />

        <Field
          label="Who it is for"
          name="purpose-text"
          value={purposeText}
          placeholder="A sentence about who it is for (optional)"
          onChange={setPurposeText}
        />
      </div>

      <div className="nlv__group">
        <h3 className="nlv__group-title">Focus</h3>

        <Field
          label="What it is about"
          name="topics"
          value={topicsText}
          placeholder="e.g. NBA, college basketball, local sports"
          hint="Separate topics with commas."
          onChange={setTopicsText}
        />
      </div>

      <div className="nlv__group">
        <h3 className="nlv__group-title">Voice</h3>

        <p className="dash__hint nlv__voice-current">{voiceSummary(newsletter)}</p>
        {newsletter.voiceNote !== undefined && newsletter.voiceNote.length > 0 && (
          <p className="dash__hint nlv__voice-tone">Tone: {newsletter.voiceNote}</p>
        )}
        <Field
          label="Writing voice"
          name="voice"
          multiline
          rows={3}
          value={voiceText}
          placeholder="Paste a sample or describe the tone to use a custom voice"
          onChange={setVoiceText}
        />
      </div>

      <div className="nlv__group">
        <h3 className="nlv__group-title">Author &amp; brand</h3>

        <Field
          label="Author name"
          name="authorName"
          value={authorName}
          placeholder="Who is writing this (a person or a brand)"
          onChange={setAuthorName}
        />

        <Field
          label="Author bio"
          name="authorBio"
          multiline
          rows={2}
          value={authorBio}
          placeholder="A short line about the author or brand"
          onChange={setAuthorBio}
        />

        <Field
          label="Sender name"
          name="fromName"
          value={fromName}
          placeholder={newsletter.name}
          hint="The name readers see in their inbox. Defaults to your newsletter's name."
          onChange={setFromName}
        />

        <Field
          label="Reply-to email"
          name="replyEmail"
          type="email"
          value={replyEmail}
          placeholder="replies@example.com"
          hint="Where replies go. Leave blank and replies are not accepted."
          onChange={setReplyEmail}
        />

        <div className="auth__field">
          <span className="auth__label">Social links</span>
          {socials.length > 0 ? (
            <ul className="nlv__social-list">
              {socials.map((item, index) => (
                <li key={index} className="nlv__social">
                  <select
                    className="auth__input nlv__social-platform"
                    value={item.platform}
                    aria-label="Social platform"
                    onChange={(event) =>
                      updateSocial(index, { platform: event.target.value as SocialPlatform })
                    }
                  >
                    {SOCIAL_PLATFORMS.map((platform) => (
                      <option key={platform} value={platform}>
                        {SOCIAL_LABELS[platform]}
                      </option>
                    ))}
                  </select>
                  <input
                    className="auth__input"
                    type="text"
                    value={item.handle}
                    placeholder="@handle or link"
                    aria-label="Social handle"
                    onChange={(event) => updateSocial(index, { handle: event.target.value })}
                  />
                  <button
                    type="button"
                    className="nlv__liked-remove"
                    aria-label={`Remove ${SOCIAL_LABELS[item.platform]} link`}
                    onClick={() => removeSocial(index)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="dash__hint">None yet. Add where readers can follow you.</p>
          )}
          <Button variant="secondary" className="nlv__social-add" onClick={addSocial}>
            Add a social link
          </Button>
        </div>
      </div>

      <div className="nlv__group">
        <h3 className="nlv__group-title">Locale</h3>

        <Field
          label="Where it is based"
          name="location"
          value={location}
          placeholder="e.g. Austin, TX"
          onChange={setLocation}
        />
        {showLocationPrompt && (
          <Notification variant="warning">
            One of your topics looks local. Add a location so our AI can find nearby coverage.
          </Notification>
        )}

        <Field
          label="Time zone"
          name="timezone"
          value={timezone}
          placeholder="e.g. America/Chicago"
          hint="We set this from your browser; edit it if it is wrong."
          onChange={setTimezone}
        />
      </div>

      <div className="nlv__group">
        <h3 className="nlv__group-title">Newsletters you like</h3>

        <div className="auth__field">
          {liked.length > 0 ? (
            <ul className="nlv__liked-list">
              {liked.map((item, index) => (
                <li key={`${item.url}-${String(index)}`} className="nlv__liked">
                  <a
                    className="dash__source-link"
                    href={item.url.includes('://') ? item.url : `https://${item.url}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item.label}
                  </a>
                  <button
                    type="button"
                    className="nlv__liked-remove"
                    aria-label={`Remove ${item.label}`}
                    onClick={() => removeLink(index)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="dash__hint">None yet. Add a few your readers would recognise.</p>
          )}
          <div className="nlv__liked-add">
            <input
              className="auth__input"
              type="text"
              value={linkLabel}
              placeholder="Name (optional)"
              aria-label="Newsletter name"
              onChange={(event) => setLinkLabel(event.target.value)}
            />
            <input
              className="auth__input"
              type="text"
              value={linkUrl}
              placeholder="https://…"
              aria-label="Newsletter link"
              onChange={(event) => setLinkUrl(event.target.value)}
            />
            <Button variant="secondary" onClick={addLink} disabled={linkUrl.trim().length === 0}>
              Add
            </Button>
          </div>
        </div>
      </div>

      <div className="nlv__save-row">
        <Button loading={saveState === 'saving'} onClick={() => void save()}>
          {saveState === 'saving' ? 'Saving…' : 'Save changes'}
        </Button>
        {saveState === 'saved' && <Notification variant="success">Saved.</Notification>}
        {saveState === 'error' && saveError !== null && (
          <Notification variant="error">{saveError}</Notification>
        )}
      </div>
    </div>
  );
}

/** A compact one-line summary of the setup, shown on the collapsed disclosure. */
function setupSummary(newsletter: Newsletter): string {
  const parts: string[] = [];
  if (newsletter.topics.length > 0) {
    parts.push(newsletter.topics.join(', '));
  }
  if (newsletter.location !== undefined && newsletter.location.length > 0) {
    parts.push(newsletter.location);
  }
  if (newsletter.cadence !== null) {
    parts.push(CADENCE_LABELS[newsletter.cadence]);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Add what this newsletter is about';
}

/**
 * The collapsed "Edit setup" disclosure wrapping the full config editor. Starts
 * open only when the essentials are missing (no topics yet), so a filled-in
 * newsletter shows just the one-line summary and does not dominate.
 */
export function SetupDisclosure({
  deps,
  newsletter,
  onUpdated,
}: {
  readonly deps: ApiDeps;
  readonly newsletter: Newsletter;
  readonly onUpdated: (updated: Newsletter) => void;
}): React.JSX.Element {
  const openByDefault = newsletter.topics.length === 0;
  return (
    <details className="nlv__panel nlv__setup" open={openByDefault}>
      <summary className="nlv__panel-summary">
        <span className="nlv__panel-heading">Edit setup</span>
        <span className="nlv__panel-hint">{setupSummary(newsletter)}</span>
      </summary>
      <div className="nlv__panel-body">
        <ConfigEditor deps={deps} newsletter={newsletter} onUpdated={onUpdated} />
      </div>
    </details>
  );
}

/** Load/generate state for the persisted draft issue behind the Design studio. */
export type IssueState = 'idle' | 'loading' | 'generating' | 'ready' | 'error';

/**
 * The persisted draft issue cache, LIFTED to the workspace so it survives the Design
 * studio unmounting on a Studio/Audience/Basics tab switch, and so a direct Audience
 * load has the draft for the Send panel (the workspace loads it once). `loaded` gates
 * the one-time auto-load (GET /issue), and `builtForKey` records the selection
 * signature the current issue was generated for, so a regenerate (POST /issue/generate)
 * fires ONLY when the curation actually changes, never merely because the owner
 * returned to Design. This keeps the billed assembly from re-firing on a tab return.
 */
export interface IssueCache {
  readonly state: IssueState;
  readonly issue: Issue | null;
  readonly error: string | null;
  /** The selection signature the current issue was (re)generated for; null until loaded. */
  readonly builtForKey: string | null;
  /** Whether GET /issue has run at least once this workspace session. */
  readonly loaded: boolean;
}

/** The initial, unloaded issue cache. */
export const EMPTY_ISSUE_CACHE: IssueCache = {
  state: 'idle',
  issue: null,
  error: null,
  builtForKey: null,
  loaded: false,
};

/** Schedule-request state for moving the newsletter into its scheduled lifecycle. */
type ScheduleState = 'idle' | 'scheduling' | 'error';

/**
 * The scheduling panel: pick a cadence, then put the first issue on the calendar.
 * Gated until at least one section exists. Copy is deliberately honest: scheduling
 * sets a target date and cadence, but YOU send each issue yourself from the Audience
 * tab (test first, then to your confirmed readers) - scheduling never auto-sends.
 */
export function LaunchPanel({
  deps,
  newsletter,
  hasSelected,
  onUpdated,
  designTo,
}: {
  readonly deps: ApiDeps;
  readonly newsletter: Newsletter;
  readonly hasSelected: boolean;
  readonly onUpdated: (updated: Newsletter) => void;
  /** Router path to the Studio, where sections are actually curated. */
  readonly designTo: string;
}): React.JSX.Element {
  const [cadence, setCadence] = useState<Cadence>(newsletter.cadence ?? 'weekly');
  const [scheduleState, setScheduleState] = useState<ScheduleState>('idle');
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  const isScheduled = newsletter.status === 'scheduled';

  // Re-sync the cadence picker when the newsletter's cadence changes upstream
  // (e.g. after scheduling elsewhere), so the control never drifts from state.
  useEffect(() => {
    if (newsletter.cadence !== null) {
      setCadence(newsletter.cadence);
    }
  }, [newsletter.cadence]);

  const runSchedule = useCallback(async (): Promise<void> => {
    setScheduleState('scheduling');
    setScheduleError(null);
    try {
      const updated = await scheduleNewsletter(deps, newsletter.newsletterId, cadence);
      onUpdated(updated);
      setScheduleState('idle');
    } catch (caught) {
      setScheduleError(
        caught instanceof Error ? caught.message : 'Could not schedule this newsletter.',
      );
      setScheduleState('error');
    }
  }, [cadence, deps, newsletter.newsletterId, onUpdated]);

  return (
    <section className="nlv__panel nlv__launch" aria-labelledby="nlv-launch">
      <h2 id="nlv-launch" className="nlv__panel-title">
        Schedule your first issue
      </h2>
      <p className="nlv__panel-lead">
        Choose how often issues go out, then put your first one on the calendar.
      </p>

      {isScheduled && newsletter.nextIssueAt !== undefined && (
        <div className="nlv__scheduled-banner" role="status">
          <p className="nlv__scheduled-headline">
            Your first issue is scheduled for {formatIssueDate(newsletter.nextIssueAt)}.
          </p>
          <p className="nlv__scheduled-note">
            This is your target date. You send each issue yourself from the Audience tab, so nothing
            goes out until you send it.
          </p>
        </div>
      )}

      <div className="nlv__launch-controls">
        <div className="nlv__cadence">
          <Field
            label="How often"
            name="cadence"
            value={cadence}
            options={CADENCES.map((option) => ({ value: option, label: CADENCE_LABELS[option] }))}
            onChange={(value) => setCadence(value as Cadence)}
          />
        </div>

        <div className="nlv__launch-actions">
          <Button
            loading={scheduleState === 'scheduling'}
            onClick={() => void runSchedule()}
            disabled={!hasSelected}
          >
            {scheduleState === 'scheduling'
              ? 'Scheduling…'
              : isScheduled
                ? 'Update schedule'
                : 'Schedule it'}
          </Button>
        </div>
      </div>

      {!hasSelected && (
        <p className="dash__hint">
          Keep at least one section before you schedule.{' '}
          <Link className="nlv__inline-link" to={designTo}>
            Choose sections in the Studio
          </Link>
        </p>
      )}

      {!isScheduled && (
        <p className="dash__hint nlv__launch-honesty">
          Scheduling sets a target date and cadence. It never sends on its own: you send each issue
          yourself from the Audience tab, so you are always in control of what goes out and when.
        </p>
      )}

      {scheduleState === 'error' && scheduleError !== null && (
        <Notification variant="error">{scheduleError}</Notification>
      )}
    </section>
  );
}

/**
 * How many of a list are real, opted-in recipients: only `confirmed` subscribers
 * (never pending/unsubscribed/suppressed) receive a send.
 */
export function confirmedCount(subscribers: readonly Subscriber[]): number {
  return subscribers.filter((subscriber) => subscriber.status === 'confirmed').length;
}

/**
 * Whether a draft has content worth sending, mirroring the backend's rule (a subject
 * AND at least one kept, non-dismissed block) so the UI gates the send before the
 * round trip instead of relying on the 400 alone.
 */
export function issueIsSendable(issue: Issue | null): boolean {
  return (
    issue !== null &&
    issue.subject.trim().length > 0 &&
    issue.blocks.some((block) => block.status !== 'dismissed')
  );
}

/** Send-request state for the send-test and the real fan-out (kept separate). */
type SendRequestState = 'idle' | 'sending' | 'sent' | 'error';

/**
 * The Send panel: the OPERATE-side affordance to actually send. Two paths, both
 * requiring a sendable draft:
 *  - "Send a test to yourself" -> POST /issue/send-test, delivering ONE copy to the
 *    signed-in owner, so you can see how the issue lands in a real inbox first.
 *  - "Send to your subscribers" -> POST /issue/send, an IRREVERSIBLE fan-out to every
 *    CONFIRMED subscriber, gated behind an explicit confirm dialog and disabled when
 *    there are no confirmed subscribers. The backend returns fast with a queued
 *    status (the fan-out is async), so we acknowledge with the confirmed count the
 *    panel already holds rather than a per-recipient result.
 */
export function SendPanel({
  deps,
  newsletterId,
  subscribers,
  subscribersReady,
  issue,
  ownerEmail,
  designTo,
}: {
  readonly deps: ApiDeps;
  readonly newsletterId: string;
  /** The hoisted subscriber list, reused for the confirmed count (never refetched here). */
  readonly subscribers: readonly Subscriber[];
  /** Whether the subscriber list has finished loading, so a count is trustworthy. */
  readonly subscribersReady: boolean;
  /** The current persisted draft issue, or null when none has been drafted yet. */
  readonly issue: Issue | null;
  /** The signed-in owner's email, shown in the test-send confirmation. */
  readonly ownerEmail?: string;
  /** Router path to the Studio, where the issue is actually drafted. */
  readonly designTo: string;
}): React.JSX.Element {
  const confirmed = useMemo(() => confirmedCount(subscribers), [subscribers]);
  const sendable = issueIsSendable(issue);

  const [testState, setTestState] = useState<SendRequestState>('idle');
  const [testMessage, setTestMessage] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  // The blast is a two-step commit: a click reveals the confirm dialog, and only a
  // confirm fires the (irreversible) send. `sentCount` freezes the count we
  // acknowledged so the banner reads stably even if the list changes afterwards.
  const [confirming, setConfirming] = useState(false);
  const [blastState, setBlastState] = useState<SendRequestState>('idle');
  const [blastError, setBlastError] = useState<string | null>(null);
  const [sentCount, setSentCount] = useState(0);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const runTest = useCallback(async (): Promise<void> => {
    if (testState === 'sending') return;
    setTestState('sending');
    setTestError(null);
    setTestMessage(null);
    try {
      await sendIssueTest(deps, newsletterId);
      if (!mounted.current) return;
      setTestState('sent');
      setTestMessage(
        ownerEmail !== undefined && ownerEmail.length > 0
          ? `Test sent to ${ownerEmail}. Check your inbox.`
          : 'Test sent to your inbox.',
      );
    } catch (caught) {
      if (!mounted.current) return;
      setTestState('error');
      setTestError(caught instanceof Error ? caught.message : 'Could not send the test.');
    }
  }, [deps, newsletterId, ownerEmail, testState]);

  const runBlast = useCallback(async (): Promise<void> => {
    if (blastState === 'sending') return;
    setConfirming(false);
    setBlastState('sending');
    setBlastError(null);
    try {
      await sendIssue(deps, newsletterId);
      if (!mounted.current) return;
      setSentCount(confirmed);
      setBlastState('sent');
    } catch (caught) {
      if (!mounted.current) return;
      setBlastState('error');
      setBlastError(caught instanceof Error ? caught.message : 'Could not start the send.');
    }
  }, [blastState, confirmed, deps, newsletterId]);

  const blastDisabled = confirmed === 0 || blastState === 'sending';

  return (
    <section className="nlv__panel nlv__send" aria-labelledby="nlv-send">
      <h2 id="nlv-send" className="nlv__panel-title">
        Send this issue
      </h2>
      <p className="nlv__panel-lead">
        Send yourself a test to see the real issue in your inbox, then send it out to your confirmed
        subscribers when you are ready.
      </p>

      {!sendable ? (
        <p className="dash__hint nlv__send-gate">
          Draft an issue in the studio first, then come back here to send it.{' '}
          <Link className="nlv__inline-link" to={designTo}>
            Draft your issue in the Studio
          </Link>
        </p>
      ) : (
        <>
          <div className="nlv__send-group">
            <h3 className="nlv__group-title">Send a test to yourself</h3>
            <p className="dash__hint">
              Delivers one copy of the current draft to your own email, so you can see how the issue
              lands in an inbox before you send it out.
            </p>
            <Button
              variant="secondary"
              loading={testState === 'sending'}
              onClick={() => void runTest()}
            >
              {testState === 'sending' ? 'Sending test…' : 'Send a test to yourself'}
            </Button>
            {testState === 'sent' && testMessage !== null && (
              <Notification variant="success">{testMessage}</Notification>
            )}
            {testState === 'error' && testError !== null && (
              <Notification variant="error">{testError}</Notification>
            )}
          </div>

          <div className="nlv__send-group">
            <h3 className="nlv__group-title">Send to your list</h3>
            {!subscribersReady ? (
              <p className="dash__hint">Checking your confirmed subscribers…</p>
            ) : (
              <>
                {confirmed === 0 ? (
                  <p className="dash__hint nlv__send-gate">
                    You have no readers to send to yet. Add or import subscribers on the left and
                    they are ready to receive this issue right away. Self-signups join once they
                    confirm.
                  </p>
                ) : (
                  <p className="dash__hint">
                    This goes out to your{' '}
                    <strong>
                      {confirmed} confirmed {confirmed === 1 ? 'reader' : 'readers'}
                    </strong>
                    . Self-signups who have not confirmed, plus unsubscribed and suppressed
                    addresses, are never sent to.
                  </p>
                )}

                {blastState === 'sent' ? (
                  <Notification variant="success" role="status">
                    Sending to {sentCount} {sentCount === 1 ? 'subscriber' : 'subscribers'}. This
                    runs in the background, so you can leave this page.
                  </Notification>
                ) : confirming ? (
                  <div className="nlv__send-confirm" role="alertdialog" aria-label="Confirm send">
                    <p className="nlv__send-confirm-text">
                      Send this issue to {confirmed} confirmed{' '}
                      {confirmed === 1 ? 'subscriber' : 'subscribers'}? This cannot be undone.
                    </p>
                    <div className="nlv__send-confirm-actions">
                      <Button
                        variant="primary"
                        loading={blastState === 'sending'}
                        onClick={() => void runBlast()}
                      >
                        {blastState === 'sending' ? 'Sending…' : 'Yes, send it'}
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={blastState === 'sending'}
                        onClick={() => setConfirming(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="primary"
                    disabled={blastDisabled}
                    onClick={() => {
                      setBlastError(null);
                      setConfirming(true);
                    }}
                  >
                    Send to your subscribers
                  </Button>
                )}

                {blastState === 'error' && blastError !== null && (
                  <Notification variant="error">{blastError}</Notification>
                )}
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/** One turn in the refine chat log: who spoke, what they said, and any applied edits. */
export interface ChatTurn {
  readonly id: number;
  readonly role: 'owner' | 'assistant';
  readonly text: string;
  /** For an assistant turn, the human "what changed" summary lines (chips). */
  readonly changed?: readonly string[];
}

/** A few example prompts to lower the blank-box friction. On-brand, no em dashes. */
const REFINE_EXAMPLES = [
  'Make the tone more casual',
  'Add a section about playoffs',
  'Drop the forum section',
  'Lead with an analysis section',
] as const;

/**
 * The refine chat: the owner talks to our AI in plain language to refine the
 * newsletter, and the server applies bounded structured edits to the config and
 * sections. Each turn appends to a short in-memory conversation (not persisted
 * across reloads, which is fine for v1), shows our AI's reply plus a "what changed"
 * summary, and re-syncs the newsletter + sections from the response so the rest of
 * the view (setup, sources, preview) reflects the edits live.
 */
export function RefineChat({
  deps,
  newsletterId,
  onNewsletterChange,
  onSectionsChange,
  turns,
  onTurnsChange,
  input,
  onInputChange,
  nextId,
}: {
  readonly deps: ApiDeps;
  readonly newsletterId: string;
  readonly onNewsletterChange: (newsletter: Newsletter) => void;
  readonly onSectionsChange: (sections: readonly SuggestedSection[]) => void;
  /** The conversation log, HOISTED to the workspace so it survives the Design
   *  studio unmounting on a tab switch. */
  readonly turns: readonly ChatTurn[];
  readonly onTurnsChange: Dispatch<SetStateAction<readonly ChatTurn[]>>;
  /** The unsent draft message, also hoisted so a half-typed prompt survives. */
  readonly input: string;
  readonly onInputChange: Dispatch<SetStateAction<string>>;
  /** A monotonic turn-id counter, hoisted so ids stay unique across remounts. */
  readonly nextId: React.MutableRefObject<number>;
}): React.JSX.Element {
  // `sending`/`error` are transient per-request state and stay local: an in-flight
  // refine turn is not expected to survive a tab switch.
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A synchronous in-flight latch (a ref, NOT React state) so two same-tick submits
  // (e.g. a double click, or Enter plus a click) can never fire two concurrent
  // /refine calls: state updates are async and would both read the old value.
  const inFlight = useRef(false);
  // The scroll container for the chat log, so each new turn scrolls into view.
  const logRef = useRef<HTMLUListElement | null>(null);

  // Keep the newest turn visible: the 20rem log otherwise hides the latest reply
  // and its chips below the fold.
  useEffect(() => {
    const log = logRef.current;
    if (log !== null) {
      log.scrollTop = log.scrollHeight;
    }
  }, [turns]);

  const send = useCallback(
    async (raw: string): Promise<void> => {
      const message = raw.trim();
      if (message.length === 0 || inFlight.current) {
        return;
      }
      inFlight.current = true;
      setError(null);
      onInputChange('');
      const ownerTurn: ChatTurn = { id: nextId.current++, role: 'owner', text: message };
      onTurnsChange((prev) => [...prev, ownerTurn]);
      setSending(true);
      try {
        const result = await refineNewsletter(deps, newsletterId, message);
        onTurnsChange((prev) => [
          ...prev,
          {
            id: nextId.current++,
            role: 'assistant',
            text: result.reply,
            changed: result.changed,
          },
        ]);
        // Re-sync the rest of the view from the applied edits.
        onNewsletterChange(result.newsletter);
        onSectionsChange(result.sections);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Our AI could not answer just now.');
      } finally {
        setSending(false);
        inFlight.current = false;
      }
    },
    [
      deps,
      newsletterId,
      nextId,
      onInputChange,
      onNewsletterChange,
      onSectionsChange,
      onTurnsChange,
    ],
  );

  return (
    <section className="nlv__panel nlv__refine" aria-labelledby="nlv-refine">
      <h2 id="nlv-refine" className="nlv__panel-title">
        Refine with our AI
      </h2>
      <p className="nlv__panel-lead">
        Tell our AI what to change in plain language and it will update this newsletter for you.
      </p>

      {turns.length > 0 && (
        <ul className="nlv__chat-log" aria-label="Conversation with our AI" ref={logRef}>
          {turns.map((turn) => (
            <li
              key={turn.id}
              className={`nlv__chat-turn nlv__chat-turn--${turn.role}`}
              data-role={turn.role}
            >
              <p className="nlv__chat-text">{turn.text}</p>
              {turn.changed !== undefined &&
                (turn.changed.length > 0 ? (
                  <ul className="nlv__chat-changes" aria-label="What changed">
                    {turn.changed.map((line, index) => (
                      <li key={index} className="nlv__chat-chip">
                        {line}
                      </li>
                    ))}
                  </ul>
                ) : (
                  // An explicit line so an assistant turn that changed nothing is
                  // never mistaken for a pending or failed one.
                  <p className="nlv__chat-nochange">No changes made</p>
                ))}
            </li>
          ))}
        </ul>
      )}

      {sending && (
        <p className="dash__status" role="status">
          Our AI is working on that…
        </p>
      )}

      {error !== null && <Notification variant="error">{error}</Notification>}

      <form
        className="nlv__chat-form"
        onSubmit={(event) => {
          event.preventDefault();
          void send(input);
        }}
      >
        <input
          className="auth__input nlv__chat-input"
          type="text"
          value={input}
          placeholder="Ask our AI to change something…"
          aria-label="Ask our AI to change something"
          disabled={sending}
          onChange={(event) => onInputChange(event.target.value)}
        />
        <Button type="submit" loading={sending} disabled={input.trim().length === 0}>
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </form>

      <div className="nlv__chat-examples" role="group" aria-label="Example prompts">
        <span className="dash__hint">Try:</span>
        {REFINE_EXAMPLES.map((example) => (
          <button
            key={example}
            type="button"
            className="nlv__chat-example"
            disabled={sending}
            onClick={() => void send(example)}
          >
            {example}
          </button>
        ))}
      </div>
    </section>
  );
}

/**
 * Split a pasted block into RAW candidate lines, one per line OR comma/semicolon/
 * whitespace-separated, trimmed with blanks dropped but deliberately NOT lowercased
 * or de-duplicated. These go to the server verbatim so its import breakdown
 * (already-on-list, duplicate-in-paste, invalid) reconciles with EXACTLY what the
 * owner pasted; normalization + validation are the server's job.
 */
export function splitImportLines(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const line = raw.trim();
    if (line.length > 0) {
      out.push(line);
    }
  }
  return out;
}

/**
 * A tolerant client-side check of whether a line LOOKS like an email, used ONLY to
 * count how many pasted lines are ready to import so the count never includes
 * obvious non-emails. The server re-validates and remains the source of truth.
 */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** A one-line, reconcilable summary of an import result for the success banner. */
export function summarizeImport(result: SubscriberImportResult): string {
  const parts = [`Added ${String(result.added)}`];
  if (result.skippedExisting > 0) {
    parts.push(`${String(result.skippedExisting)} already on your list`);
  }
  if (result.skippedDuplicateInPaste > 0) {
    parts.push(`${String(result.skippedDuplicateInPaste)} duplicate in your paste`);
  }
  if (result.invalid > 0) {
    parts.push(`${String(result.invalid)} invalid`);
  }
  return `${parts.join(', ')}.`;
}

/** Load state for the subscribers panel. */
type SubscribersState = 'loading' | 'ready' | 'error';

/**
 * The subscribers list cache, LIFTED to the workspace so the loaded list survives
 * the Operate surface unmounting on a tab switch and is not refetched on return.
 */
export interface SubscribersCache {
  readonly state: SubscribersState;
  readonly subscribers: readonly Subscriber[];
  readonly error: string | null;
  /** Whether the list has been fetched at least once this session. */
  readonly loaded: boolean;
}

/** The initial, unloaded subscribers cache. */
export const EMPTY_SUBSCRIBERS_CACHE: SubscribersCache = {
  state: 'loading',
  subscribers: [],
  error: null,
  loaded: false,
};

/**
 * The Subscribers panel: a secondary, self-contained section for MANAGING the
 * newsletter's list. The owner can add one subscriber, paste a list to import, see
 * the count + list with a status badge per row, and remove anyone. Copy is
 * deliberately honest and CURRENT: an OWNER add/import lands the reader "confirmed"
 * and sendable at once (the owner asserts they opted in) and emails NOTHING. Only a
 * PUBLIC self-signup through the newsletter's own page is "pending" until they click
 * the double-opt-in confirmation link. It does not touch the setup -> structure ->
 * preview -> schedule -> chat flow above it.
 */
export function SubscribersPanel({
  deps,
  newsletterId,
  cache,
  onCacheChange,
  email,
  onEmailChange,
}: {
  readonly deps: ApiDeps;
  readonly newsletterId: string;
  /** The hoisted list cache, owned by the workspace so it survives a tab switch. */
  readonly cache: SubscribersCache;
  readonly onCacheChange: Dispatch<SetStateAction<SubscribersCache>>;
  /** The hoisted "add a subscriber" draft email, so a typed-but-unsent value survives. */
  readonly email: string;
  readonly onEmailChange: Dispatch<SetStateAction<string>>;
}): React.JSX.Element {
  const { state, error: loadError, subscribers } = cache;

  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importNote, setImportNote] = useState<string | null>(null);
  const [importInvalidLines, setImportInvalidLines] = useState<readonly string[]>([]);

  // File import runs alongside the paste importer: its own chosen-file + busy +
  // error state, but it reuses the SAME success note + invalid-lines display so the
  // breakdown reads identically however the list was imported.
  const [importFile, setImportFile] = useState<File | null>(null);
  const [fileImporting, setFileImporting] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [removingId, setRemovingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async (): Promise<void> => {
    onCacheChange((prev) => ({ ...prev, state: 'loading', error: null }));
    try {
      const result = await listSubscribers(deps, newsletterId);
      if (!mounted.current) return;
      onCacheChange((prev) => ({
        ...prev,
        state: 'ready',
        subscribers: result.subscribers,
        loaded: true,
      }));
    } catch (caught) {
      if (!mounted.current) return;
      onCacheChange((prev) => ({
        ...prev,
        state: 'error',
        error: caught instanceof Error ? caught.message : 'Could not load your subscribers.',
      }));
    }
  }, [deps, newsletterId, onCacheChange]);

  // Fetch once per session. If the list was already loaded before an Operate ->
  // Design -> Operate round trip, the hoisted cache is reused and we do not refetch.
  useEffect(() => {
    if (!cache.loaded) void load();
  }, [cache.loaded, load]);

  const importLines = useMemo(() => splitImportLines(importText), [importText]);
  // Only lines that look like emails are counted as "ready"; obvious non-emails are
  // surfaced separately so the count never over-promises what will actually import.
  const readyCount = useMemo(() => importLines.filter(looksLikeEmail).length, [importLines]);
  const notEmailCount = importLines.length - readyCount;

  // Clear a lingering "Added N…" import banner whenever the list is mutated another
  // way (an add or a remove), so a stale success note never sits over a changed list.
  const clearImportNote = useCallback((): void => {
    setImportNote(null);
    setImportInvalidLines([]);
  }, []);

  const add = useCallback(async (): Promise<void> => {
    const value = email.trim();
    if (value.length === 0 || adding) {
      return;
    }
    setAdding(true);
    setAddError(null);
    clearImportNote();
    try {
      const subscriber = await addSubscriber(deps, newsletterId, value);
      if (!mounted.current) return;
      // Prepend so the newest addition is visible without a reload.
      onCacheChange((prev) => ({ ...prev, subscribers: [subscriber, ...prev.subscribers] }));
      onEmailChange('');
    } catch (caught) {
      if (!mounted.current) return;
      setAddError(caught instanceof Error ? caught.message : 'Could not add that subscriber.');
    } finally {
      if (mounted.current) setAdding(false);
    }
  }, [adding, clearImportNote, deps, email, newsletterId, onCacheChange, onEmailChange]);

  const runImport = useCallback(async (): Promise<void> => {
    // Nothing valid to send: guard so an all-invalid paste never fires a request.
    if (importing || readyCount === 0) {
      return;
    }
    setImporting(true);
    setImportError(null);
    // Clear the other importer's error too, so its stale banner never lingers
    // under this action's result (only one importer's outcome is ever current).
    setFileError(null);
    setImportNote(null);
    setImportInvalidLines([]);
    try {
      // Send the RAW lines so the server can categorize + echo the full breakdown.
      const result = await importSubscribers(deps, newsletterId, importLines);
      if (!mounted.current) return;
      setImportNote(summarizeImport(result));
      setImportInvalidLines(result.invalidLines ?? []);
      setImportText('');
      // Re-fetch so the freshly imported rows appear in the list + count.
      await load();
    } catch (caught) {
      if (!mounted.current) return;
      setImportError(caught instanceof Error ? caught.message : 'Could not import those emails.');
    } finally {
      if (mounted.current) setImporting(false);
    }
  }, [deps, importing, importLines, load, newsletterId, readyCount]);

  const runFileImport = useCallback(async (): Promise<void> => {
    // Guard: nothing chosen, or an import already in flight.
    if (importFile === null || fileImporting) {
      return;
    }
    setFileImporting(true);
    setFileError(null);
    setImportError(null);
    setImportNote(null);
    setImportInvalidLines([]);
    try {
      // The client picks the Content-Type/kind, uploads straight to S3, then asks
      // the server to parse + import, returning the same breakdown as a paste.
      const result = await importSubscribersFromFile(deps, newsletterId, importFile);
      if (!mounted.current) return;
      setImportNote(summarizeImport(result));
      setImportInvalidLines(result.invalidLines ?? []);
      setImportFile(null);
      if (fileInputRef.current !== null) {
        fileInputRef.current.value = '';
      }
      // Re-fetch so the freshly imported rows appear in the list + count, exactly
      // as the paste path does.
      await load();
    } catch (caught) {
      if (!mounted.current) return;
      setFileError(caught instanceof Error ? caught.message : 'Could not import that file.');
    } finally {
      if (mounted.current) setFileImporting(false);
    }
  }, [deps, fileImporting, importFile, load, newsletterId]);

  const remove = useCallback(
    async (subscriber: Subscriber): Promise<void> => {
      // Removing frees the email's uniqueness guard, so confirm before doing it.
      if (!window.confirm(`Remove ${subscriber.email} from your list?`)) {
        return;
      }
      setRemovingId(subscriber.subscriberId);
      setRowError(null);
      clearImportNote();
      try {
        await removeSubscriber(deps, newsletterId, subscriber.subscriberId);
        if (!mounted.current) return;
        onCacheChange((prev) => ({
          ...prev,
          subscribers: prev.subscribers.filter((s) => s.subscriberId !== subscriber.subscriberId),
        }));
      } catch (caught) {
        if (!mounted.current) return;
        setRowError(caught instanceof Error ? caught.message : 'Could not remove that subscriber.');
      } finally {
        if (mounted.current) setRemovingId(null);
      }
    },
    [clearImportNote, deps, newsletterId, onCacheChange],
  );

  const count = subscribers.length;

  return (
    <section className="nlv__panel nlv__subscribers" aria-labelledby="nlv-subscribers">
      <div className="nlv__subscribers-head">
        <h2 id="nlv-subscribers" className="nlv__panel-title">
          Subscribers
        </h2>
        {/* Only show the count once the list has actually loaded, so it never
            flashes a stale or 0 count over a loading/errored panel. */}
        {state === 'ready' && (
          <span className="nlv__subscriber-count" aria-label={`${String(count)} subscribers`}>
            {count}
          </span>
        )}
      </div>
      <p className="nlv__panel-lead">
        Build your list here. Everyone you add or import counts as a reader you can send to right
        away, since you are confirming they opted in. People who subscribe themselves through your
        newsletter&rsquo;s page get a confirmation email first and join once they confirm.
      </p>

      <form
        className="nlv__sub-add"
        onSubmit={(event) => {
          event.preventDefault();
          void add();
        }}
      >
        <div className="nlv__sub-add-field">
          <Field
            label="Add a subscriber"
            name="subscriber-email"
            type="email"
            value={email}
            placeholder="reader@example.com"
            disabled={adding}
            onChange={onEmailChange}
          />
        </div>
        <Button
          type="submit"
          aria-label="Add subscriber"
          loading={adding}
          disabled={email.trim().length === 0}
        >
          {adding ? 'Adding…' : 'Add'}
        </Button>
      </form>
      {addError !== null && <Notification variant="error">{addError}</Notification>}

      <details className="nlv__sub-import">
        <summary className="nlv__sub-import-summary">Import a list</summary>
        <div className="nlv__sub-import-body">
          <Field
            label="Paste emails"
            name="import-emails"
            multiline
            rows={4}
            value={importText}
            placeholder="One per line, or comma-separated"
            disabled={importing}
            onChange={setImportText}
            hint={
              readyCount > 0
                ? `${String(readyCount)} ${readyCount === 1 ? 'email' : 'emails'} ready to import` +
                  (notEmailCount > 0
                    ? `; ${String(notEmailCount)} ${notEmailCount === 1 ? 'line does' : 'lines do'} not look like an email.`
                    : '.')
                : importLines.length > 0
                  ? 'None of these look like an email yet.'
                  : 'Paste one email per line or separate them with commas.'
            }
          />
          <Button
            loading={importing}
            onClick={() => void runImport()}
            disabled={fileImporting || readyCount === 0}
          >
            {importing ? 'Importing…' : 'Import'}
          </Button>

          <p className="nlv__sub-import-or" aria-hidden="true">
            or upload a file
          </p>

          <label className="auth__field nlv__sub-file-field">
            <span className="auth__label">Import from a file</span>
            <input
              ref={fileInputRef}
              className="auth__input nlv__sub-file-input"
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              aria-label="Subscriber file to import"
              disabled={fileImporting}
              onChange={(event) => {
                setFileError(null);
                // Also clear a stale paste error so it can't sit under this result.
                setImportError(null);
                setImportNote(null);
                setImportInvalidLines([]);
                setImportFile(event.target.files?.[0] ?? null);
              }}
            />
            <span className="dash__hint">
              {importFile !== null
                ? `Ready to import ${importFile.name}`
                : 'Upload a .csv or .txt file of email addresses.'}
            </span>
          </label>
          <Button
            loading={fileImporting}
            onClick={() => void runFileImport()}
            disabled={importing || importFile === null}
          >
            {fileImporting ? 'Importing…' : 'Import file'}
          </Button>
          {fileError !== null && <Notification variant="error">{fileError}</Notification>}
        </div>
      </details>
      {importNote !== null && <Notification variant="success">{importNote}</Notification>}
      {importInvalidLines.length > 0 && (
        <p className="dash__hint nlv__sub-invalid" role="status">
          Skipped as invalid: {importInvalidLines.join(', ')}
        </p>
      )}
      {importError !== null && <Notification variant="error">{importError}</Notification>}

      {state === 'loading' && (
        <p className="dash__status" role="status">
          Loading your subscribers…
        </p>
      )}

      {state === 'error' && (
        <>
          <Notification variant="error">{loadError}</Notification>
          <Button variant="secondary" className="app__btn-start" onClick={() => void load()}>
            Try again
          </Button>
        </>
      )}

      {rowError !== null && <Notification variant="error">{rowError}</Notification>}

      {state === 'ready' &&
        (count === 0 ? (
          <p className="dash__empty">No subscribers yet. Add or import your list.</p>
        ) : (
          <ul className="nlv__sub-list" aria-label="Subscribers">
            {subscribers.map((subscriber) => (
              <li key={subscriber.subscriberId} className="nlv__sub-row">
                <span className="nlv__sub-email" title={subscriber.email}>
                  {subscriber.email}
                </span>
                <Badge
                  status={subscriber.status}
                  variant="tag"
                  {...(subscriber.status === 'pending'
                    ? {
                        title:
                          'This reader subscribed themselves and has not confirmed yet. They count as a reader once they confirm.',
                      }
                    : {})}
                />
                <button
                  type="button"
                  className="nlv__sub-remove"
                  aria-label={`Remove ${subscriber.email}`}
                  disabled={removingId === subscriber.subscriberId}
                  onClick={() => void remove(subscriber)}
                >
                  {removingId === subscriber.subscriberId ? 'Removing…' : 'Remove'}
                </button>
              </li>
            ))}
          </ul>
        ))}
    </section>
  );
}

/** Load state for the read-only sent-issue history panel. */
type SentIssuesState = 'loading' | 'ready' | 'error';

/**
 * The Sent issues panel: a calm, READ-ONLY record of what actually went out to
 * readers, newest first. Sending is live and irreversible, so this is the owner's
 * honest history of past sends. Each row shows the subject (the primary text), a
 * friendly sent date, and how many readers it reached. It loads once on mount - a
 * plain tenant read with nothing billed - and mirrors the SubscribersPanel's
 * loading/error/empty states. v1 is read-only: no delete, no resend, no per-issue
 * open/click analytics.
 */
export function SentIssuesPanel({
  deps,
  newsletterId,
}: {
  readonly deps: ApiDeps;
  readonly newsletterId: string;
}): React.JSX.Element {
  const [state, setState] = useState<SentIssuesState>('loading');
  const [sentIssues, setSentIssues] = useState<readonly SentIssue[]>([]);
  const [error, setError] = useState<string | null>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async (): Promise<void> => {
    setState('loading');
    setError(null);
    try {
      const result = await listSentIssues(deps, newsletterId);
      if (!mounted.current) return;
      setSentIssues(result.sentIssues);
      setState('ready');
    } catch (caught) {
      if (!mounted.current) return;
      setError(caught instanceof Error ? caught.message : 'Could not load your sent issues.');
      setState('error');
    }
  }, [deps, newsletterId]);

  // Load once on mount. This is a plain, un-billed read, so a refetch on a tab
  // return is harmless; no hoisted cache is needed (unlike the billed subscriber list).
  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="nlv__panel nlv__sent" aria-labelledby="nlv-sent">
      <h2 id="nlv-sent" className="nlv__panel-title">
        Sent issues
      </h2>
      <p className="nlv__panel-lead">
        A record of the issues you have sent to your readers, newest first.
      </p>

      {state === 'loading' && (
        <p className="dash__status" role="status">
          Loading your sent issues…
        </p>
      )}

      {state === 'error' && (
        <>
          <Notification variant="error">{error}</Notification>
          <Button variant="secondary" onClick={() => void load()}>
            Try again
          </Button>
        </>
      )}

      {state === 'ready' &&
        (sentIssues.length === 0 ? (
          <p className="dash__empty">
            You haven&rsquo;t sent any issues yet. When you send an issue to your readers, it shows
            up here.
          </p>
        ) : (
          <ul className="nlv__sent-list" aria-label="Sent issues">
            {sentIssues.map((issue) => (
              <li key={issue.sendId} className="nlv__sent-row">
                <span className="nlv__sent-subject" title={issue.subject}>
                  {issue.subject}
                </span>
                <span className="nlv__sent-meta">
                  <span className="nlv__sent-date">{formatIssueDate(issue.sentAt)}</span>
                  <span className="nlv__sent-readers">
                    {issue.recipientCount} {issue.recipientCount === 1 ? 'reader' : 'readers'}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        ))}
    </section>
  );
}
